import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Record<string, string> | undefined} */
  #preferredSelections;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('keydown', this.#handleSwatchKeyboardActivation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleSwatchKeyboardActivation);
  }

  /**
   * Enables keyboard activation on swatch chips.
   * @param {KeyboardEvent} event
   */
  #handleSwatchKeyboardActivation = (event) => {
    const label = event.target instanceof HTMLElement ? event.target.closest('.variant-option__button-label') : null;
    if (!(label instanceof HTMLLabelElement) || label.getAttribute('aria-disabled') === 'true') return;
    const isSpaceKey = event.key === ' ' || event.key === 'Spacebar' || event.key === 'Space' || event.code === 'Space';
    const isEnterKey = event.key === 'Enter' || event.code === 'Enter' || event.code === 'NumpadEnter';
    if (!isSpaceKey && !isEnterKey) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;
    // Sold-out values that are rendered selectable (no aria-disabled) can be chosen by keyboard, like a click.
    if (input.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    if (input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const changedOptionName = this.getOptionNameFromTarget(event.target);
    if (this.isStickyOption(changedOptionName)) {
      this.setStickySelection(changedOptionName, this.getTargetValue(event.target));
    }

    this.updateSelectedOption(event.target);
    this.#preferredSelections = this.getPreferredSelections();
    this.syncPendingSelections(changedOptionName);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    const variantId = this.getTargetVariantId(event.target);
    this.fetchUpdatedSection(this.buildRequestUrl(event.target, null, [], variantId), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], explicitVariantId = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    // If we already know the exact variant id (common on size switches), request directly.
    // This avoids slower option_values resolution paths and extra round trips.
    if (explicitVariantId) {
      params.push(`variant=${explicitVariantId}`);
    }

    if (!explicitVariantId && this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (!explicitVariantId && source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Returns the variant id encoded on a changed input/select target when available.
   * @param {HTMLElement} target
   * @returns {string | null}
   */
  getTargetVariantId(target) {
    // A value with no variant under the current selection (3X while 5" is selected) carries the
    // closest real variant instead, so the request lands on an existing combination.
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return target.dataset.variantId || target.dataset.fallbackVariantId || null;
    }

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      return selectedOption?.dataset.variantId || selectedOption?.dataset.fallbackVariantId || null;
    }

    return null;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    const preferredSelections = this.#preferredSelections;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const abortController = this.#abortController;

    fetch(requestUrl, { signal: abortController.signal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        let html = this.parseResponseHtml(responseText);

        const resolvedRequestUrl = this.resolvePreferredRequestUrl(html, requestUrl, preferredSelections);
        if (resolvedRequestUrl && resolvedRequestUrl !== requestUrl) {
          const resolvedResponse = await fetch(resolvedRequestUrl, { signal: abortController.signal });
          const resolvedResponseText = await resolvedResponse.text();
          html = this.parseResponseHtml(resolvedResponseText);
        }

        // Find the matching variant-picker in the returned HTML, scoped by current element dataset
        const scopedSelector = this.buildScopedSelector();

        const scopedPicker =
          html.querySelector(scopedSelector) ||
          // Fallback to first variant-picker if a perfect match isn't found
          html.querySelector('variant-picker');

        const textContent = scopedPicker?.querySelector(`script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html, preferredSelections);
        } else {
          const newProduct = this.updateVariantPicker(html, scopedSelector, preferredSelections);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        this.clearPendingSelections();
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Parses fetched HTML and normalizes one-off attributes.
   * @param {string} responseText
   * @returns {Document}
   */
  parseResponseHtml(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    // Defer is only useful for the initial rendering of the page. Remove it here.
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    return html;
  }

  /**
   * Resolves the exact request URL for remembered selections on the newly loaded picker.
   * This prevents the server from rendering the fallback size first.
   * @param {Document} html
   * @param {string} requestUrl
   * @param {Record<string, string> | undefined} preferredSelections
   * @returns {string | null}
   */
  resolvePreferredRequestUrl(html, requestUrl, preferredSelections) {
    if (!preferredSelections) return null;

    const stickySelections = Object.entries(preferredSelections).filter(([optionName]) => this.isStickyOption(optionName));
    if (!stickySelections.length) return null;

    const scopedPicker = html.querySelector(this.buildScopedSelector()) || html.querySelector('variant-picker');
    if (!(scopedPicker instanceof HTMLElement)) return null;

    for (const [optionName, preferredValue] of stickySelections) {
      const option = Array.from(scopedPicker.querySelectorAll('.variant-option')).find((element) => {
        return element.getAttribute('data-option-name')?.toLowerCase() === optionName;
      });

      if (!(option instanceof HTMLElement)) continue;

      const checkedInput = option.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement && checkedInput.value === preferredValue) return null;

      const matchingInput = Array.from(option.querySelectorAll('input[type="radio"]')).find((input) => {
        if (!(input instanceof HTMLInputElement)) return false;

        return (
          input.value === preferredValue &&
          !input.disabled &&
          input.getAttribute('aria-disabled') !== 'true' &&
          !!input.dataset.variantId
        );
      });

      if (matchingInput instanceof HTMLInputElement && matchingInput.dataset.variantId) {
        const resolvedUrl = new URL(requestUrl, window.location.origin);
        resolvedUrl.searchParams.set('variant', matchingInput.dataset.variantId);
        resolvedUrl.searchParams.delete('option_values');
        return resolvedUrl.toString();
      }

      const select = option.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) continue;

      const matchingOption = Array.from(select.options).find((inputOption) => {
        return inputOption.value === preferredValue && !inputOption.disabled && !!inputOption.dataset.variantId;
      });

      if (!matchingOption?.dataset.variantId) continue;

      const resolvedUrl = new URL(requestUrl, window.location.origin);
      resolvedUrl.searchParams.set('variant', matchingOption.dataset.variantId);
      resolvedUrl.searchParams.delete('option_values');
      return resolvedUrl.toString();
    }

    return null;
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @param {string} [scopedSelector] - Scoped selector used to find the matching picker in newHtml.
   * @param {Record<string, string>} [preferredSelections]
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml, scopedSelector, preferredSelections = undefined) {
    /** @type {NewProduct | undefined} */
    let newProduct;
    // Preserve current UI state and references before morph
    const wasCollapsed = this.classList.contains('is-collapsed');
    const prevForm = this.querySelector('.variant-picker__form');
    const prevFormId = prevForm instanceof HTMLFormElement ? prevForm.id : null;

    // Prefer a scoped selector (block/product id) to avoid cross-updating the wrong picker
    let newVariantPickerSource;
    if (scopedSelector) {
      newVariantPickerSource =
        newHtml.querySelector(scopedSelector) || newHtml.querySelector(this.tagName.toLowerCase());
    } else {
      // Build a scoped selector from current element as a fallback
      const parts = [];
      if (this.dataset.blockId) parts.push(`[data-block-id="${this.dataset.blockId}"]`);
      if (this.dataset.productId) parts.push(`[data-product-id="${this.dataset.productId}"]`);
      const builtSelector = 'variant-picker' + (parts.length ? parts.join('') : '');
      newVariantPickerSource =
        newHtml.querySelector(builtSelector) || newHtml.querySelector(this.tagName.toLowerCase());
    }

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.restorePreferredSelections(preferredSelections);

    // Restore expanded/collapsed UI state after morph (server HTML may default to collapsed)
    if (wasCollapsed) {
      this.classList.add('is-collapsed');
    } else {
      this.classList.remove('is-collapsed');
    }

    // Keep external toggle buttons (with + icon) wired to the current form id, even if it changed
    try {
      const newForm = this.querySelector('.variant-picker__form');
      const newFormId = newForm instanceof HTMLFormElement ? newForm.id : null;
      if (prevFormId && newFormId && prevFormId !== newFormId) {
        const toggles = document.querySelectorAll(
          `.variant-picker__mobile-toggle[aria-controls="${prevFormId}"], .variant-picker__desktop-toggle[aria-controls="${prevFormId}"], .variant-picker__card-trigger[aria-controls="${prevFormId}"]`
        );
        toggles.forEach((toggle) => {
          toggle.setAttribute('aria-controls', newFormId);
          // Sync aria-expanded and icon to match current state
          const expanded = !this.classList.contains('is-collapsed');
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          const icon = toggle.querySelector('.variant-picker__mobile-toggle-icon');
          if (icon) icon.textContent = expanded ? '−' : '+';
        });
      } else if (newFormId) {
        // Ensure aria-expanded reflects current state even if id did not change
        const toggles = document.querySelectorAll(
          `.variant-picker__mobile-toggle[aria-controls="${newFormId}"], .variant-picker__desktop-toggle[aria-controls="${newFormId}"], .variant-picker__card-trigger[aria-controls="${newFormId}"]`
        );
        const expanded = !this.classList.contains('is-collapsed');
        toggles.forEach((toggle) => {
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          const icon = toggle.querySelector('.variant-picker__mobile-toggle-icon');
          if (icon) icon.textContent = expanded ? '−' : '+';
        });
      }
    } catch (e) {
      // no-op: do not break if toggles are missing
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   * @param {Record<string, string>} [preferredSelections]
   */
  updateMain(newHtml, preferredSelections = undefined) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);

    requestAnimationFrame(() => {
      const nextPicker =
        document.querySelector(this.buildScopedSelector(false)) || document.querySelector('variant-picker');
      if (nextPicker instanceof VariantPicker) {
        nextPicker.restorePreferredSelections(preferredSelections);
      }
    });
  }

  /**
   * Builds a selector that identifies the current picker instance.
   * @param {boolean} [includeProductId]
   * @returns {string}
   */
  buildScopedSelector(includeProductId = true) {
    const parts = [];
    if (this.dataset.blockId) parts.push(`[data-block-id="${this.dataset.blockId}"]`);
    if (includeProductId && this.dataset.productId) parts.push(`[data-product-id="${this.dataset.productId}"]`);

    return 'variant-picker' + (parts.length ? parts.join('') : '');
  }

  /**
   * Gets currently selected option values keyed by option name.
   * @returns {Record<string, string>}
   */
  getSelectedOptionsByName() {
    /** @type {Record<string, string>} */
    const selections = {};

    this.querySelectorAll('.variant-option').forEach((option) => {
      const optionName = option.getAttribute('data-option-name')?.toLowerCase();
      if (!optionName) return;

      const checkedInput = option.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement) {
        selections[optionName] = checkedInput.value;
        return;
      }

      const select = option.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        selections[optionName] = select.value;
      }
    });

    return selections;
  }

  /**
   * Gets current selections merged with sticky remembered selections.
   * Sticky values override current rendered values when available.
   * @returns {Record<string, string>}
   */
  getPreferredSelections() {
    return {
      ...this.getSelectedOptionsByName(),
      ...this.getStickySelections(),
    };
  }

  /**
   * Adds a temporary visual selected state while the refreshed picker is loading.
   * @param {string | null} changedOptionName
   */
  syncPendingSelections(changedOptionName) {
    this.clearPendingSelections();

    if (this.isStickyOption(changedOptionName)) return;

    Object.entries(this.getStickySelections()).forEach(([optionName, optionValue]) => {
      this.applyPendingSelection(optionName, optionValue);
    });
  }

  /**
   * Returns the option name for the changed target.
   * @param {HTMLElement} target
   * @returns {string | null}
   */
  getOptionNameFromTarget(target) {
    return target.closest('.variant-option')?.getAttribute('data-option-name')?.toLowerCase() ?? null;
  }

  /**
   * Returns whether an option should be remembered across rerenders.
   * @param {string | null} optionName
   * @returns {boolean}
   */
  isStickyOption(optionName) {
    return !!optionName && optionName.includes('size');
  }

  /**
   * Gets the selected value from an input/select target.
   * @param {HTMLElement} target
   * @returns {string | null}
   */
  getTargetValue(target) {
    if (target instanceof HTMLInputElement) return target.value;
    if (target instanceof HTMLSelectElement) return target.value;

    return null;
  }

  /**
   * Builds a stable key for remembered picker selections.
   * @returns {string}
   */
  getStickySelectionKey() {
    const parts = [this.dataset.sectionId || 'section', this.dataset.blockId || 'block'];
    const productCard = this.closest('.product-card');

    if (productCard instanceof HTMLElement) {
      parts.push(productCard.dataset.productId || productCard.getAttribute('href') || 'card');
    } else {
      parts.push('main');
    }

    return parts.join('::');
  }

  /**
   * Returns the shared sticky selection store.
   * @returns {Record<string, Record<string, string>>}
   */
  getStickySelectionStore() {
    /** @type {Window & { __variantPickerStickySelections?: Record<string, Record<string, string>> }} */
    const variantWindow = window;
    variantWindow.__variantPickerStickySelections = variantWindow.__variantPickerStickySelections || {};

    return variantWindow.__variantPickerStickySelections;
  }

  /**
   * Gets remembered sticky selections for this picker.
   * @returns {Record<string, string>}
   */
  getStickySelections() {
    return this.getStickySelectionStore()[this.getStickySelectionKey()] || {};
  }

  /**
   * Remembers a sticky selection for this picker.
   * @param {string | null} optionName
   * @param {string | null} optionValue
   */
  setStickySelection(optionName, optionValue) {
    if (!optionName || !optionValue) return;

    const store = this.getStickySelectionStore();
    const key = this.getStickySelectionKey();

    store[key] = {
      ...(store[key] || {}),
      [optionName]: optionValue,
    };
  }

  /**
   * Removes temporary visual selected states.
   */
  clearPendingSelections() {
    this.querySelectorAll('.variant-option--pending-selection').forEach((option) => {
      option.classList.remove('variant-option--pending-selection');
    });

    this.querySelectorAll('.variant-option__button-label--pending-selected').forEach((label) => {
      label.classList.remove('variant-option__button-label--pending-selected');
    });
  }

  /**
   * Applies the remembered selection immediately while the refreshed picker is loading.
   * @param {string} optionName
   * @param {string} optionValue
   */
  applyPendingSelection(optionName, optionValue) {
    if (!optionName || !optionValue) return;

    const option = Array.from(this.querySelectorAll('.variant-option')).find((element) => {
      return element.getAttribute('data-option-name')?.toLowerCase() === optionName;
    });

    if (!(option instanceof HTMLElement)) return;

    const matchingInput = Array.from(option.querySelectorAll('input[type="radio"]')).find((input) => {
      if (!(input instanceof HTMLInputElement)) return false;

      return (
        input.value === optionValue &&
        !input.disabled &&
        input.getAttribute('aria-disabled') !== 'true'
      );
    });

    if (matchingInput instanceof HTMLInputElement) {
      this.updateSelectedOption(matchingInput);
    }

    const matchingLabel = matchingInput?.closest('.variant-option__button-label');
    if (matchingLabel instanceof HTMLElement) {
      option.classList.add('variant-option--pending-selection');
      matchingLabel.classList.add('variant-option__button-label--pending-selected');
      return;
    }

    const select = option.querySelector('select');
    if (!(select instanceof HTMLSelectElement)) return;

    const matchingOption = Array.from(select.options).find((inputOption) => {
      return inputOption.value === optionValue && !inputOption.disabled;
    });

    if (!matchingOption) return;

    select.value = matchingOption.value;
    this.updateSelectedOption(select);
  }

  /**
   * Restores preferred option values after the picker is re-rendered.
   * @param {Record<string, string> | undefined} preferredSelections
   */
  restorePreferredSelections(preferredSelections) {
    if (!preferredSelections) return;

    this.clearPendingSelections();

    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let changedTarget = null;

    Object.entries(preferredSelections).forEach(([optionName, preferredValue]) => {
      if (!preferredValue) return;

      const option = Array.from(this.querySelectorAll('.variant-option')).find((element) => {
        return element.getAttribute('data-option-name')?.toLowerCase() === optionName;
      });

      if (!(option instanceof HTMLElement)) return;

      const checkedInput = option.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement && checkedInput.value === preferredValue) return;

      const matchingInput = Array.from(option.querySelectorAll('input[type="radio"]')).find((input) => {
        if (!(input instanceof HTMLInputElement)) return false;

        // A variant id means the combination exists (sold out is fine). Without one, restoring the
        // previous value would re-request a combination that does not exist (3X with 5") and the
        // fallback would bounce the picker back to another size.
        return (
          input.value === preferredValue &&
          !input.disabled &&
          input.getAttribute('aria-disabled') !== 'true' &&
          !!input.dataset.variantId
        );
      });

      if (matchingInput instanceof HTMLInputElement) {
        matchingInput.checked = true;
        changedTarget = matchingInput;
        return;
      }

      const select = option.querySelector('select');
      if (!(select instanceof HTMLSelectElement)) return;

      const matchingOption = Array.from(select.options).find((inputOption) => {
        return inputOption.value === preferredValue && !inputOption.disabled && !!inputOption.dataset.variantId;
      });

      if (!matchingOption || select.value === matchingOption.value) return;

      select.value = matchingOption.value;
      this.updateSelectedOption(select);
      changedTarget = select;
    });

    this.#preferredSelections = undefined;

    /** @type {any} */
    const targetToDispatch = changedTarget;
    if (targetToDispatch && 'dispatchEvent' in targetToDispatch) {
      targetToDispatch.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
